import Phaser from "phaser";
import { playSound } from "../utils/sound-utils";

interface ButtonConfig {
  scene: Phaser.Scene;
  x: number;
  y: number;
  text: string;
  style?: "primary" | "secondary" | "success" | "danger" | "orange" | "discord" | "warning";
  disabled?: boolean;
  iconOnly?: boolean;
  icon?: string;
  iconFrame?: number;
  useSmallLabel?: boolean;
  size?: "normal" | "small" | "icon";
  onClick: () => void;
}

const BUTTON_HEIGHT = 36; // Fixed height for normal buttons
const SMALL_BUTTON_HEIGHT = 28; // Fixed height for small buttons
const BUTTON_PADDING = 8; // Horizontal padding for text buttons

export class ModernButton extends Phaser.GameObjects.Container {
  private background!: Phaser.GameObjects.Graphics;
  private border!: Phaser.GameObjects.Graphics;
  private shadow!: Phaser.GameObjects.Graphics;
  private buttonText!: Phaser.GameObjects.Text;
  private config: ButtonConfig;
  private isHovered: boolean = false;
  private isPressed: boolean = false;
  public buttonWidth: number = 0; // Calculated width based on content
  private baseScale: number = 1; // Track the base scale for hover calculations

  constructor(config: ButtonConfig) {
    super(config.scene, config.x, config.y);

    this.config = {
      style: "primary",
      disabled: false,
      ...config,
    };

    this.createButton();
    this.setupInteractions();

    // Register with scene so it's in the display list
    config.scene.add.existing(this);
  }

  private createButton(): void {
    const { text, style, iconOnly, icon, iconFrame, useSmallLabel, size } = this.config;
    const buttonHeight = size === "small" ? SMALL_BUTTON_HEIGHT : BUTTON_HEIGHT;

    // Create temporary text to measure width
    const fontSize = size === "small" ? "16px" : size === "icon" ? "20px" : (useSmallLabel ? "12px" : "30px");

    // Calculate button width
    if (iconOnly) {
      // Icon-only buttons are square (buttonHeight x buttonHeight)
      this.buttonWidth = buttonHeight;
    } else {
      const textStyle: Phaser.Types.GameObjects.Text.TextStyle = {
        fontSize: fontSize,
        fontFamily: "Anton, sans-serif",
        color: "#ffffff",
        fontStyle: "600", // semibold
        align: (useSmallLabel || size === "icon") ? "center" : undefined,
      };
      const tempText = this.scene.add.text(0, 0, text, textStyle);
      this.buttonWidth = tempText.width + BUTTON_PADDING * 2;
      tempText.destroy();
    }

    // Create shadow (drawn first, appears behind)
    this.shadow = new Phaser.GameObjects.Graphics(this.scene);
    this.shadow.fillStyle(0x000000, 0.7);
    if (iconOnly) {
      // Circular shadow for icon-only buttons
      const radius = this.buttonWidth / 2;
      this.shadow.fillCircle(3, 6, radius);
    } else {
      this.shadow.fillRoundedRect(
        -this.buttonWidth / 2 + 3,
        -buttonHeight / 2 + 6,
        this.buttonWidth,
        buttonHeight,
        6,
      );
    }
    this.add(this.shadow);

    // Create border
    this.border = new Phaser.GameObjects.Graphics(this.scene);
    this.add(this.border);

    // Create background
    this.background = new Phaser.GameObjects.Graphics(this.scene);
    this.add(this.background);

    // Create text or icon
    if (iconOnly && icon) {
      // Add icon image
      const iconImage = iconFrame !== undefined
        ? new Phaser.GameObjects.Image(this.scene, 0, 0, icon, iconFrame)
        : new Phaser.GameObjects.Image(this.scene, 0, 0, icon);
      iconImage.setDisplaySize(this.buttonWidth * 0.6, this.buttonWidth * 0.6);
      iconImage.setOrigin(0.5);
      this.add(iconImage);
    } else {
      // Create text FIRST before calling updateButtonAppearance
      const textStyle: Phaser.Types.GameObjects.Text.TextStyle = {
        fontSize: fontSize,
        fontFamily: "Anton, sans-serif",
        color: "#ffffff",
        fontStyle: "600", // semibold
        align: (useSmallLabel || size === "icon") ? "center" : undefined,
      };

      this.buttonText = new Phaser.GameObjects.Text(
        this.scene,
        0,
        -2,
        text,
        textStyle,
      );
      this.buttonText.setOrigin(0.5);
      this.add(this.buttonText);
    }

    // NOW we can safely call updateButtonAppearance since buttonText exists
    this.updateButtonAppearance();
  }

  private updateButtonAppearance(): void {
    const { style, disabled, iconOnly, size } = this.config;
    const buttonHeight = size === "small" ? SMALL_BUTTON_HEIGHT : BUTTON_HEIGHT;

    this.background.clear();
    this.border.clear();

    // Update border color based on style and disabled state
    let borderColor = 0x6b7280; // gray-500 (default)
    if (disabled) {
      borderColor = 0x4b5563; // darker gray for disabled
    } else if (style === "success") {
      borderColor = 0x14532d; // green-900 for success buttons
    } else if (style === "danger") {
      borderColor = 0x7f1d1d; // red-900 for danger buttons
    } else if (style === "orange") {
      borderColor = 0xb8600b; // orange-900 for orange buttons
    } else if (style === "warning") {
      borderColor = 0x8a6d1a; // muted gold border for warning buttons
    } else if (style === "discord") {
      borderColor = 0x3a3f9e; // discord dark border
    }
    this.border.lineStyle(4, borderColor, disabled ? 0.5 : 1);

    if (iconOnly) {
      // Circular border for icon-only buttons
      const radius = this.buttonWidth / 2;
      this.border.strokeCircle(0, 0, radius);
    } else {
      this.border.strokeRoundedRect(
        -this.buttonWidth / 2,
        -buttonHeight / 2,
        this.buttonWidth,
        buttonHeight,
        6,
      );
    }

    if (disabled) {
      // Disabled state - always gray and muted
      this.background.fillStyle(0x374151, 0.5); // gray-700 with reduced opacity
      if (this.buttonText) {
        this.buttonText.setAlpha(0.5); // Make text more transparent
      }
    } else {
      if (this.buttonText) {
        this.buttonText.setAlpha(1); // Full opacity for enabled buttons
      }

      if (style === "primary") {
        if (this.isPressed) {
          this.background.fillStyle(0x4b5563, 1); // gray-600 (darker)
        } else if (this.isHovered) {
          this.background.fillStyle(0x4b5563, 1); // gray-600
        } else {
          this.background.fillStyle(0x374151, 1); // gray-700
        }
      } else if (style === "success") {
        if (this.isPressed) {
          this.background.fillStyle(0x15803d, 1); // green-700 (darker)
        } else if (this.isHovered) {
          this.background.fillStyle(0x16a34a, 1); // green-600
        } else {
          this.background.fillStyle(0x22c55e, 1); // green-500
        }
      } else if (style === "danger") {
        if (this.isPressed) {
          this.background.fillStyle(0xb91c1c, 1); // red-700 (darker)
        } else if (this.isHovered) {
          this.background.fillStyle(0xdc2626, 1); // red-600
        } else {
          this.background.fillStyle(0xef4444, 1); // red-500
        }
      } else if (style === "orange") {
        if (this.isPressed) {
          this.background.fillStyle(0xcc6600, 1); // orange-700 (darker)
        } else if (this.isHovered) {
          this.background.fillStyle(0xff9900, 1); // orange-600
        } else {
          this.background.fillStyle(0xffaa00, 1); // orange-500
        }
      } else if (style === "discord") {
        if (this.isPressed) {
          this.background.fillStyle(0x4752c4, 1); // discord pressed
        } else if (this.isHovered) {
          this.background.fillStyle(0x5865f2, 1); // discord hover
        } else {
          this.background.fillStyle(0x5865f2, 1); // discord blurple
        }
      } else if (style === "warning") {
        if (this.isPressed) {
          this.background.fillStyle(0xccad24, 1); // yellow (darker, pressed)
        } else if (this.isHovered) {
          this.background.fillStyle(0xffe680, 1); // yellow (lighter, hover)
        } else {
          this.background.fillStyle(0xffe066, 1); // yellow-500 (COLORS.YELLOW)
        }
      } else {
        // Secondary style
        if (this.isPressed) {
          this.background.fillStyle(0x1f2937, 1); // gray-800 (darker, pressed)
        } else if (this.isHovered) {
          this.background.fillStyle(0x4b5563, 1); // gray-600 (lighter, hover)
        } else {
          this.background.fillStyle(0x374151, 1); // gray-700
        }
      }
    }

    if (iconOnly) {
      // Circular background for icon-only buttons
      const radius = this.buttonWidth / 2;
      this.background.fillCircle(0, 0, radius);
    } else {
      this.background.fillRoundedRect(
        -this.buttonWidth / 2,
        -buttonHeight / 2,
        this.buttonWidth,
        buttonHeight,
        6,
      );
    }
  }

  private setupInteractions(): void {
    const buttonHeight = this.config.size === "small" ? SMALL_BUTTON_HEIGHT : BUTTON_HEIGHT;

    if (this.config.iconOnly) {
      // Circular hit area for icon-only buttons
      const radius = this.buttonWidth / 2;
      this.setInteractive(
        new Phaser.Geom.Circle(0, 0, radius),
        Phaser.Geom.Circle.Contains,
      );
    } else {
      this.setInteractive(
        new Phaser.Geom.Rectangle(
          -this.buttonWidth / 2,
          -buttonHeight / 2,
          this.buttonWidth,
          buttonHeight,
        ),
        Phaser.Geom.Rectangle.Contains,
      );
    }

    // Set cursor to pointer only if not disabled
    this.on("pointerover", () => {
      if (this.config.disabled) return; // Skip hover effects if disabled

      if (this.scene && this.scene.input) {
        this.scene.input.setDefaultCursor("pointer");
      }
      this.isHovered = true;
      this.updateButtonAppearance();

      // Scale up slightly from base scale (use super to avoid updating baseScale)
      const hoverScale = this.baseScale * 1.05;

      // Add a subtle tween for smooth scaling
      if (this.scene && this.scene.tweens) {
        this.scene.tweens.killTweensOf(this);
        this.scene.tweens.add({
          targets: this,
          scaleX: hoverScale,
          scaleY: hoverScale,
          duration: 200,
          ease: "Power2",
        });
      }
    });

    this.on("pointerout", () => {
      if (this.config.disabled) return; // Skip hover effects if disabled

      if (this.scene && this.scene.input) {
        this.scene.input.setDefaultCursor("default");
      }
      this.isHovered = false;
      this.isPressed = false;
      this.updateButtonAppearance();

      // Scale back to base scale
      if (this.scene && this.scene.tweens) {
        this.scene.tweens.killTweensOf(this);
        super.setScale(this.baseScale);
        this.scene.tweens.add({
          targets: this,
          scaleX: this.baseScale,
          scaleY: this.baseScale,
          duration: 200,
          ease: "Power2",
        });
      }
    });

    this.on("pointerdown", () => {
      if (this.config.disabled) return; // Skip click effects if disabled

      this.isPressed = true;
      this.updateButtonAppearance();

      // Scale up on click for feedback
      const pressScale = this.baseScale * 1.5;
      if (this.scene && this.scene.tweens) {
        this.scene.tweens.killTweensOf(this);
        super.setScale(pressScale);
        this.scene.tweens.add({
          targets: this,
          scaleX: pressScale,
          scaleY: pressScale,
          duration: 100,
          ease: "Power2",
        });
      }
    });

    this.on("pointerup", () => {
      if (this.config.disabled) return; // Skip click effects if disabled

      if (this.isPressed && this.config.onClick) {
        const text = this.config.text.toLowerCase();
        const isShopAction = text.startsWith("sell") || text.startsWith("reload");
        playSound(this.scene, isShopAction ? "shop-sound" : "menu-click-sound");
        this.config.onClick();
      }
      this.isPressed = false;
      this.updateButtonAppearance();

      // Scale back to base scale when not hovered
      if (this.scene && this.scene.tweens) {
        this.scene.tweens.killTweensOf(this);
        super.setScale(this.baseScale);
        this.scene.tweens.add({
          targets: this,
          scaleX: this.baseScale,
          scaleY: this.baseScale,
          duration: 100,
          ease: "Power2",
        });
      }
    });
  }

  public setText(newText: string): void {
    this.buttonText.setText(newText);
  }

  public setStyle(style: "primary" | "secondary" | "success" | "danger" | "orange" | "discord" | "warning"): void {
    this.config.style = style;
    this.isHovered = false;
    this.isPressed = false;
    this.updateButtonAppearance();
  }

  public setWidth(newWidth: number): void {
    this.buttonWidth = newWidth;
    this.updateShadow();
    this.updateButtonAppearance();
  }

  private updateShadow(): void {
    const { iconOnly } = this.config;
    const buttonHeight = this.config.size === "small" ? SMALL_BUTTON_HEIGHT : BUTTON_HEIGHT;

    this.shadow.clear();
    this.shadow.fillStyle(0x000000, 0.7);
    if (iconOnly) {
      // Circular shadow for icon-only buttons
      const radius = this.buttonWidth / 2;
      this.shadow.fillCircle(3, 6, radius);
    } else {
      this.shadow.fillRoundedRect(
        -this.buttonWidth / 2 + 3,
        -buttonHeight / 2 + 6,
        this.buttonWidth,
        buttonHeight,
        6,
      );
    }
  }

  public setButtonStyle(style: "primary" | "secondary"): void {
    this.config.style = style;
    this.updateButtonAppearance();
  }

  public setDisabled(disabled: boolean): void {
    this.config.disabled = disabled;
    this.isHovered = false;
    this.isPressed = false;
    this.updateButtonAppearance();

    // Reset scale if button was being hovered when disabled
    if (disabled && this.scene && this.scene.tweens) {
      this.scene.tweens.killTweensOf(this);
      this.setScale(this.baseScale);
    }
  }

  public setScale(x: number, y?: number): this {
    // Update base scale when manually setting scale (called from outside)
    this.baseScale = x;
    return super.setScale(x, y);
  }

  public destroy(): void {
    if (this.scene && this.scene.tweens) {
      this.scene.tweens.killTweensOf(this);
    }
    super.destroy();
  }
}
